/**
 * Token Refresh Service
 * Handles automatic token refresh and activity detection
 */

import updateToken from '@/apis/refreshToken';
import { useUserStore } from '@/stores/user.state';

class TokenRefreshService {
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private activityTimer: ReturnType<typeof setTimeout> | null = null;
  private isRefreshing = false;
  private refreshPromise: Promise<any> | null = null;
  
  // Configuration
  private readonly TOKEN_REFRESH_BEFORE_EXPIRY = 5 * 60 * 1000; // 5 minutes before expiry
  private readonly ACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes of inactivity
  private readonly CHECK_INTERVAL = 60 * 1000; // Check every minute
  
  private lastActivity: number = Date.now();

  constructor() {
    this.setupActivityListeners();
  }

  /**
   * Initialize the token refresh mechanism
   */
  public initialize() {
    this.scheduleTokenRefresh();
    this.startActivityMonitoring();
  }

  /**
   * Setup activity listeners
   */
  private setupActivityListeners() {
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      window.addEventListener(event, () => this.recordActivity(), { passive: true });
    });
  }

  /**
   * Record user activity
   */
  private recordActivity() {
    this.lastActivity = Date.now();
  }

  /**
   * Check if user is active
   */
  private isUserActive(): boolean {
    const inactiveDuration = Date.now() - this.lastActivity;
    return inactiveDuration < this.ACTIVITY_TIMEOUT;
  }

  /**
   * Start monitoring user activity
   */
  private startActivityMonitoring() {
    if (this.isDoctorUser()) {
      // Doctors should stay logged in until they explicitly logout
      return;
    }

    if (this.activityTimer) {
      clearInterval(this.activityTimer);
    }

    this.activityTimer = setInterval(() => {
      if (!this.isUserActive()) {
        this.handleInactiveLogout();
      }
    }, this.CHECK_INTERVAL);
  }

  /**
   * Handle logout due to inactivity
   */
  private handleInactiveLogout() {
    if (this.isDoctorUser()) {
      return;
    }

    const userStore = useUserStore();
    userStore.onLogout();
    this.cleanup();
  }

  /**
   * Get token expiry time from JWT
   */
  private getTokenExpiryTime(): number | null {
    const token = localStorage.getItem('accessToken');
    if (!token) return null;

    try {
      const parsedToken = JSON.parse(token);
      const payload = JSON.parse(atob(parsedToken.split('.')[1]));
      return payload.exp ? payload.exp * 1000 : null; // Convert to milliseconds
    } catch (error) {
      return null;
    }
  }

  /**
   * Schedule token refresh before expiry
   */
  private scheduleTokenRefresh() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    const expiryTime = this.getTokenExpiryTime();
    if (!expiryTime) {
      return;
    }

    const currentTime = Date.now();
    const timeUntilExpiry = expiryTime - currentTime;
    const refreshTime = timeUntilExpiry - this.TOKEN_REFRESH_BEFORE_EXPIRY;

    if (refreshTime <= 0) {
      // Token already expired or will expire soon
      this.refreshToken();
    } else {
      this.refreshTimer = setTimeout(() => {
        this.refreshToken();
      }, refreshTime);
    }
  }

  /**
   * Refresh the access token
   */
  public async refreshToken(): Promise<boolean> {
    // If already refreshing, return the existing promise
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    
    this.refreshPromise = (async () => {
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (!refreshToken) {
        this.handleRefreshFailure();
        return false;
      }

      try {
        const parsedRefreshToken = JSON.parse(refreshToken);
        const response = await updateToken({ refreshToken: parsedRefreshToken });
        
        if (response.data?.data?.accessToken) {
          const newAccessToken = response.data.data.accessToken;
          const newRefreshToken = response.data.data.refreshToken;
          
          // Store new tokens
          localStorage.setItem('accessToken', JSON.stringify(newAccessToken));
          if (newRefreshToken) {
            localStorage.setItem('refreshToken', JSON.stringify(newRefreshToken));
          }
          
          // Schedule next refresh
          this.scheduleTokenRefresh();
          
          return true;
        } else {
          this.handleRefreshFailure();
          return false;
        }
      } catch (error: any) {
        // If refresh token is invalid or expired, logout
        if (error.response?.status === 401 || error.response?.status === 403) {
          this.handleRefreshFailure();
        }
        
        return false;
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Handle refresh failure
   */
  private handleRefreshFailure() {
    const userStore = useUserStore();
    userStore.onLogout();
    this.cleanup();
  }

  /**
   * Cleanup timers and listeners
   */
  public cleanup() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    
    if (this.activityTimer) {
      clearInterval(this.activityTimer);
      this.activityTimer = null;
    }
  }

  /**
   * Reset and restart the service
   */
  public reset() {
    this.cleanup();
    this.lastActivity = Date.now();
    this.initialize();
  }

  /**
   * Determine if current user is a doctor (non-super admin admin)
   */
  private isDoctorUser(): boolean {
    const userStore = useUserStore();
    const user = userStore.userData as any;
    if (!user) {
      return false;
    }

    const roles: string[] = user.roles || user.roleIdentifiers || [];
    const isSuperAdmin = roles?.some(
      (role) =>
        role === 'Role.SuperAdmin' ||
        role?.toLowerCase?.().includes('superadmin')
    );

    const isDoctor = (user.userType === 'Admin' && !isSuperAdmin) ||
      roles?.some((role) => role === 'Role.Admin');

    const isResearcher = roles?.some((role) => role === 'Role.Researcher') || user.userType === 'Researcher';

    return (isDoctor && !isSuperAdmin) || isResearcher;
  }
}

// Singleton instance
export const tokenRefreshService = new TokenRefreshService();


